import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { getLoginInfo, getToken } from "../../shared/utils/storage";
import { createNotification } from "../../shared/components/Popup";
import { PAGE_GUARDS, ETD_ROLES } from "../../shared/constants/roles";

/**
 * Route guard hook.
 *
 * Performs client-side role checks to redirect unauthorized users.
 *
 * SECURITY NOTE: Frontend authorization is for user experience only.
 * Real authorization must be enforced by the backend on every API request.
 * A determined user can bypass these checks by modifying client-side code.
 */
const useRouteGuard = () => {
  const navigate = useNavigate();

  const guardPage = useCallback((pageKey) => {
    const token = getToken();
    const rolesData = getLoginInfo()?.user?.Roles || "[]";

    let roles = [];
    try {
      roles = typeof rolesData === "string" ? JSON.parse(rolesData) : rolesData;
    } catch {
      roles = [];
    }

    const hasRole = (roleCode) =>
      Array.isArray(roles) && roles.some((role) => role.RoleCode === roleCode);

    const deny = () => {
      navigate("/");
      createNotification({ type: "error", message: "Access denied" })();
    };

    if (!token) {
      deny();
      return;
    }

    switch (pageKey) {
      case PAGE_GUARDS.MAIN_DASHBOARD:
        if (!hasRole(ETD_ROLES.ADMINISTRATOR)) deny();
        break;

      case PAGE_GUARDS.ETD_DASHBOARD:
        // ETD dashboard is accessible to all authenticated ETD roles.
        break;

      case PAGE_GUARDS.ASSESSMENT:
        if (!hasRole(ETD_ROLES.ADMINISTRATOR) && !hasRole(ETD_ROLES.ASSESSOR))
          deny();
        break;

      case PAGE_GUARDS.APPROVAL:
        if (!hasRole(ETD_ROLES.ADMINISTRATOR) && !hasRole(ETD_ROLES.APPROVAL))
          deny();
        break;

      case PAGE_GUARDS.MANAGER:
        if (!hasRole(ETD_ROLES.ADMINISTRATOR) && !hasRole(ETD_ROLES.MANAGER))
          deny();
        break;

      case PAGE_GUARDS.UNLOCK_APPLICATION:
      case PAGE_GUARDS.UNLOCK_DOCUMENT:
        if (
          !hasRole(ETD_ROLES.ADMINISTRATOR) &&
          !hasRole(ETD_ROLES.MANAGER) &&
          !hasRole(ETD_ROLES.STATION_ADMIN)
        )
          deny();
        break;

      case PAGE_GUARDS.ENQUIRY:
        if (
          !hasRole(ETD_ROLES.APPROVAL) &&
          !hasRole(ETD_ROLES.MANAGER) &&
          !hasRole(ETD_ROLES.ASSESSOR) &&
          !hasRole(ETD_ROLES.ADMINISTRATOR) &&
          !hasRole(ETD_ROLES.STATION_ADMIN)
        )
          deny();
        break;

      case PAGE_GUARDS.REPORTS_ADMIN:
        if (
          !hasRole(ETD_ROLES.ADMINISTRATOR) &&
          !hasRole(ETD_ROLES.MANAGER) &&
          !hasRole(ETD_ROLES.ASSESSOR)
        )
          deny();
        break;

      default:
        break;
    }
  }, []);

  return { guardPage };
};

useRouteGuard.propTypes = {
  pageKey: PropTypes.string,
};

// Backward-compatible export (old components called it "Lindo" with "mgambo" method)
const Lindo = () => {
  const { guardPage } = useRouteGuard();
  return { mgambo: guardPage };
};

export default Lindo;
export { useRouteGuard };
